import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { DataTable } from '@/components/ui/data-table';

import { useEventHandler } from '@/hooks/use-event-handler';
import { useToggle } from '@/hooks/use-toggle';

import { useBatchCallsQuery } from '@/query/scheduler.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import { BatchCallCreateDialog } from './components/batch-call-create-dialog';
import { BatchCallHeader } from './components/batch-call-header';
import { getColumns } from './components/columns';
import { BatchCallEmptyResult } from './components/empty';

import { useSchedulerListStore } from '@/store/schedule.store';
import type { BatchCall } from '@/types/scheduler.type';
import { type ColumnDef } from '@tanstack/react-table';

export default function BatchCalls() {
  const { state, toggle, open } = useToggle();

  const { setPageSize, setCurrentPage, currentPage, pageSize, sort, setSort, filters, query } =
    useSchedulerListStore();

  const { data, isLoading } = useBatchCallsQuery({
    page: currentPage,
    page_size: pageSize,
    filters,
    query,
  });

  const navigate = useNavigate();
  useEventHandler({
    name: 'batch-calls:create',
    eventHandler: open,
  });

  const batchCalls: BatchCall[] = data?.data || [];
  const meta = data?.meta;

  const columns = useMemo<ColumnDef<BatchCall>[]>(() => getColumns(), []);

  return (
    <>
      <div className="flex flex-col h-full space-y-6">
        {/** Search and Filter */}
        <BatchCallHeader batchCalls={batchCalls} />

        <div className="bg-background border rounded-lg">
          <DataTable
            columns={columns}
            data={batchCalls}
            sorting={sort}
            isLoading={isLoading}
            onSortingChange={setSort}
            onRowClick={row => navigate(`/batch-calls/${row.id}`)}
            emptyState={<BatchCallEmptyResult />}
          />

          {/* PagePagination */}
          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1); // Reset to first page when page size changes
            }}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      {/* Create Batch Call Dialog */}
      <ProtectedComponent requiredPermissions={[Permissions.BatchCallCreateUpdate]}>
        {state && <BatchCallCreateDialog toggle={toggle} />}
      </ProtectedComponent>
    </>
  );
}
